/** Shared UI primitives for the predimensionamiento subpages. */

import type { ReactNode } from 'react';

export function PredimCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="rounded-[var(--radius-md)] border p-5"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
      <div className="mb-3">
        <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</div>
        {subtitle && (
          <div className="mt-0.5 text-[11.5px]" style={{ color: 'var(--text-muted)' }}>{subtitle}</div>
        )}
      </div>
      {children}
    </div>
  );
}

export function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-3" style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function NumberRow({
  label, value, onChange, step = 1, unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  unit?: string;
}) {
  return (
    <label className="flex items-center justify-between gap-3 text-[12px]">
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={value}
          step={step}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (!Number.isNaN(v)) onChange(v);
          }}
          className="w-[110px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[12px] font-mono"
          style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
        />
        {unit && <span className="text-[10px] w-12" style={{ color: 'var(--text-muted)' }}>{unit}</span>}
      </div>
    </label>
  );
}

export function SelectRow<T extends string>({
  label, value, options, onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 text-[12px]">
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="w-[164px] rounded-[var(--radius-sm)] border px-2 py-1 text-[12px]"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

export function ResultRow({
  label, value, highlight = false, mono = true,
}: { label: string; value: string; highlight?: boolean; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-1 border-b last:border-b-0" style={{ borderColor: 'var(--border-subtle)' }}>
      <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span
        className={mono ? 'font-mono text-[13.5px]' : 'text-[13.5px]'}
        style={{
          color: highlight ? 'var(--accent)' : 'var(--text-primary)',
          fontWeight: highlight ? 700 : 500,
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function FormulaNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2.5 text-[11px] leading-relaxed"
      style={{
        background: 'rgba(59,130,246,0.06)',
        borderColor: 'rgba(59,130,246,0.25)',
        color: 'var(--text-secondary)',
      }}>
      {children}
    </div>
  );
}

/** Round to the nearest multiple. Returns the rounded value. */
export function roundTo(value: number, step: number): number {
  return Math.round(value / step) * step;
}

import { useExportPredim } from './useExport';
import { ExportCostHint } from '@/features/billing/ExportCostHint';

/** Action bar with Export PDF + Export Excel buttons. Predim exports are
 *  cheaper than full design exports — 1 fierro each. */
export function ExportButtons({
  family,
  inputs,
}: {
  family: 'columns' | 'beams' | 'slabs' | 'walls' | 'footings';
  inputs: Record<string, unknown>;
}) {
  const exporter = useExportPredim();
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={exporter.busy === 'pdf'}
        onClick={() => exporter.trigger('pdf', family, inputs)}
        className="inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border px-3 py-1.5 text-[11px] font-medium transition-colors hover:bg-[var(--surface-2)] disabled:cursor-not-allowed disabled:opacity-50"
        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {exporter.busy === 'pdf' ? 'Generando PDF…' : 'Exportar PDF'}
        <ExportCostHint cost={1} />
      </button>
      <button
        type="button"
        disabled={exporter.busy === 'xlsx'}
        onClick={() => exporter.trigger('xlsx', family, inputs)}
        className="inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border px-3 py-1.5 text-[11px] font-medium transition-colors hover:bg-[var(--surface-2)] disabled:cursor-not-allowed disabled:opacity-50"
        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {exporter.busy === 'xlsx' ? 'Generando Excel…' : 'Exportar Excel'}
        <ExportCostHint cost={1} />
      </button>
      {exporter.error && (
        <span className="text-[10px]" style={{ color: 'var(--danger)' }}>{exporter.error}</span>
      )}
    </div>
  );
}
